import { createSelector } from '@reduxjs/toolkit'
import { projectDataSelectors } from 'store/features/projectData/selectors'
import { editorSelectors } from 'store/features/editor/selectors'
import { projectSelectors } from 'store/features/project/selectors'
import { aiImageSelectors } from 'store/features/aiImage/selectors'
import { aiVideoSelectors } from 'store/features/aiVideo/selectors'
import { userSelectors } from 'store/features/user/selectors'
import { MediaStatus } from 'types/media'
import { imageTypes, PROJECT_MEDIA_TYPES } from 'consts'
import { isVisualMediaItem } from 'utils/media'
import { isAudioItem } from 'utils/audio'
import { SUB_SIDEBAR_KEYS } from 'consts/sidebar'
import { checkIfAIVideoIsGenerating } from 'containers/SubSidebar/AIVideoContent/utils'
import {
  getDefaultAIVideoModel,
  getBestFreeQualityOption
} from './features/aiVideo/utils'
import { isPremiumModel } from 'utils'
import { MIN_PREMIUM_QUALITY } from 'consts'
import { TUserPlan, USER_PLAN_TO_MAX_DURATION } from 'consts/user'

export const selectActiveMedia = createSelector(
  projectDataSelectors.mediaItems,
  editorSelectors.activeTimelineItem,
  (mediaItems, activeTimelineItem) => {
    if (
      !activeTimelineItem.id ||
      !isVisualMediaItem(activeTimelineItem.type) ||
      !mediaItems ||
      mediaItems.length === 0
    ) {
      return null
    }
    return mediaItems.find(item => item.id === activeTimelineItem.id)
  }
)

export const selectActiveAudio = createSelector(
  projectDataSelectors.multilineAudios,
  editorSelectors.activeTimelineItem,
  (audios, activeTimelineItem) => {
    if (
      !activeTimelineItem.id ||
      !isAudioItem(activeTimelineItem.type) ||
      !audios ||
      audios.length === 0
    ) {
      return null
    }
    return audios.find(item => item.id === activeTimelineItem.id)
  }
)

export const selectActiveAudioItem = createSelector(
  projectDataSelectors.multilineAudios,
  editorSelectors.activeTimelineItem,
  (audios, activeTimelineItem) => {
    if (
      !activeTimelineItem.id ||
      !isAudioItem(activeTimelineItem.type) ||
      !audios ||
      audios.length === 0
    ) {
      return null
    }
    return audios.find(item => item.id === activeTimelineItem.id)
  }
)

export const activeMediaItemImgPath = createSelector(
  selectActiveMedia,
  activeMedia =>
    activeMedia?.type && imageTypes.includes(activeMedia.type)
      ? activeMedia.meta.path
      : null
)

export const activeMediaItemAssetId = createSelector(
  selectActiveMedia,
  activeMedia => activeMedia?.assetId
)

export const activeMediaItemType = createSelector(
  selectActiveMedia,
  activeMedia => activeMedia?.type
)

export const activeMediaItemStatus = createSelector(
  selectActiveMedia,
  activeMedia => activeMedia?.meta.status
)

export const projectStyleSelector = createSelector(
  projectSelectors.metaData,
  aiImageSelectors.stylesData,
  projectSelectors.hasCustomStyle,
  projectSelectors.customStyleInfo,
  (metaData, styles, hasCustomStyle, customStyleInfo) => {
    if (hasCustomStyle && customStyleInfo) {
      return {
        thumbnailUrl: customStyleInfo.url,
        name: customStyleInfo.styleName
      }
    }
    return styles.find(style => style.id === metaData?.styleId)
  }
)

export const isVideoConsistencyFeatureAvailableSelector = createSelector(
  projectStyleSelector,
  aiVideoSelectors.isAnimate,
  projectSelectors.meta,
  (projectStyle, isAnimate, projectMeta) =>
    !!projectStyle && !isAnimate && !!projectMeta?.genAiProjectId
)

export const isActiveMediaItemPendingSelector = createSelector(
  selectActiveMedia,
  activeMedia => activeMedia?.meta.status === MediaStatus.PENDING
)

export const isAiVideoActiveSelector = createSelector(
  [editorSelectors.activeSidebarTab, isActiveMediaItemPendingSelector],
  (activeSidebarTab, isActiveMediaItemPending) =>
    activeSidebarTab === SUB_SIDEBAR_KEYS.AI_VIDEO && !isActiveMediaItemPending
)

export const isActiveAiVideoGeneratingSelector = createSelector(
  selectActiveMedia,
  activeMedia => (activeMedia ? checkIfAIVideoIsGenerating(activeMedia) : false)
)

export const isSmartEditModeSelector = createSelector(
  projectStyleSelector,
  aiVideoSelectors.isGeneration,
  editorSelectors.activeSidebarTab,
  aiVideoSelectors.aiImageId,
  aiVideoSelectors.isFromDifferentProject,
  aiVideoSelectors.targetMediaItemId,
  projectDataSelectors.mediaItems,
  aiVideoSelectors.generationParams,
  projectSelectors.meta,
  (
    projectStyle,
    isGeneration,
    activeSidebarTab,
    aiImageId,
    isFromDifferentProject,
    targetMediaItemId,
    mediaItems,
    generationParams,
    projectMeta
  ) => {
    const targetItem = mediaItems.find(item => item.id === targetMediaItemId)
    const isTargetAiVideo = targetItem?.type === PROJECT_MEDIA_TYPES.aiVideo
    const isTargetPending = targetItem?.meta?.status === MediaStatus.PENDING
    const isGenAiProject = !!projectMeta?.genAiProjectId
    // In gen-ai projects, items without sourceProjectId weren't generated in this project
    const isNotFromCurrentGenAiProject =
      isGenAiProject && !generationParams?.sourceProjectId

    return (
      !!projectStyle &&
      !isGeneration &&
      activeSidebarTab === SUB_SIDEBAR_KEYS.AI_VIDEO &&
      !!targetMediaItemId &&
      !!aiImageId &&
      // Disable smart edit for history items created in a different project,
      // as their style context doesn't match the current project
      !isFromDifferentProject &&
      // In gen-ai projects, only items generated in this project get smart edit
      !isNotFromCurrentGenAiProject &&
      // Smart edit only applies to AI video items, not images being animated
      isTargetAiVideo &&
      // Don't show smart edit while the video is still generating
      !isTargetPending
    )
  }
)

export const projectDefaultVideoModel = createSelector(
  [
    aiVideoSelectors.aiVideoModels,
    projectSelectors.metaData,
    userSelectors.userCurrentPlanId,
    userSelectors.isFreeUser,
    userSelectors.unlimitedAiVideoModels
  ],
  (models, metaData, userCurrentPlanId, isFreeUser, unlimitedVideoModels) => {
    if (!models.length)
      return {
        modelCode: '',
        videoGenerationCost: 0
      }
    const defaultModel = getDefaultAIVideoModel(models)
    const metaModel = models.find(model => model.model === metaData?.videoModel)

    const currentModel =
      metaModel && !isPremiumModel(metaModel, userCurrentPlanId)
        ? metaModel
        : defaultModel

    const { duration, quality } = currentModel.defaultValues
    const defaultOption = currentModel.options.find(
      option => option.duration === duration
    )

    const resolvedQuality =
      isFreeUser && quality >= MIN_PREMIUM_QUALITY && defaultOption
        ? (getBestFreeQualityOption(defaultOption.qualities)?.quality ??
          quality)
        : quality

    const unlimitedModel = unlimitedVideoModels.find(
      m => m.aiVideoModelId === currentModel.id
    )
    const isUnlimited = unlimitedModel?.qualities.some(
      q => q.value === resolvedQuality
    )

    const creditsPerDefaultValues = isUnlimited
      ? 0
      : defaultOption?.qualities.find(
          option => option.quality === resolvedQuality
        )?.credit || 0

    return {
      modelCode: currentModel.model,
      videoGenerationCost: creditsPerDefaultValues
    }
  }
)

export const isAllowedDurationSelector = createSelector(
  projectDataSelectors.duration,
  userSelectors.userCurrentPlanId,
  (duration, userCurrentPlanId) => {
    const allowedDuration =
      USER_PLAN_TO_MAX_DURATION[userCurrentPlanId as TUserPlan]
    return duration <= allowedDuration
  }
)

export const isActiveMediaItemGeneratingSelector = createSelector(
  selectActiveMedia,
  activeMedia =>
    activeMedia ? activeMedia.meta.status === MediaStatus.PENDING : false
)
